import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { urlConstants } from '../../../constants/urlConstants';

export interface VirtualSiteVisitItem {
  _id: string;
  firstName: string;
  lastName?: string;
  email?: string | null;
  phoneNumber: string;
  primaryMobCountryCode?: string;
  alternateNumber?: string | null;
  secondaryMobCountryCode?: string | null;
  currentResidentArea?: string | null;
  pinCode?: string | null;
  project?: any;
  projectName?: string | null;
  customerType?: string;
  bookedCustomer?: boolean;
  unitNumber?: string | null;
  purposeOfVisit?: string | null;
  preferredUnit?: any;
  preferredBudgetRange?: any;
  minPrice?: string | null;
  maxPrice?: string | null;
  sourceOfInformation?: any;
  ageRange?: any;
  occupation?: any;
  preferredSize?: any;
  purposeOfPurchase?: any;
  primarySource?: string | null;
  secondarySource?: string | null;
  socialMedia?: string | null;
  referredCPName?: string | null;
  referredEmpDept?: string | null;
  referredUnitNo?: string | null;
  referredContactNo?: string | null;
  referredMobCountryCode?: string | null;
  referredEmpName?: string | null;
  referredFriendName?: string | null;
  referredProjectName?: string | null;
  referredEmpId?: string | null;
  otherReferrence?: string | null;
  existingUserProjectName?: string | null;
  existingUserUnitNo?: string | null;
  ownerEmail?: string | null;
  flsEmail?: string | null;
  isNRI?: number;
  channelPartnerSignatureUrl?: string | null;
  salesExecutiveSignatureUrl?: string | null;
  customerSignatureUrl?: string | null;
  isManualTagging?: boolean;
  manualTaggingDate?: string | null;
  manualTaggingTime?: string | null;
  note?: string | null;
  zone?: string | null;
  customerName?: string | null;
  foreignContactNumber?: string | null;
  foreignMobCountryCode?: string | null;
  indianContactNumber?: string | null;
  indianMobCountryCode?: string | null;
  cityOfResidence?: string | null;
  countryOfResidence?: string | null;
  zipPostalCode?: string | null;
  companyName?: string | null;
  designation?: string | null;
  propertyLookingFor?: string | null;
  homeType?: string | null;
  homeTypeCustom?: string | null;
  familySize?: string | null;
  monthlySalaryRange?: string | null;
  recordStatus?: string;
  source?: string | null;
  tertiarySource?: string | null;
  isOtpVerified?: boolean;
  salesforceApiStatus: 'Pending' | 'Success' | 'Failed';
  salesforceApiPayload?: any;
  salesforceApiResponse?: any;
  salesforceApiCalledAt?: string | null;
  salesforceApiError?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VirtualSiteVisitListResponse {
  statusCode: number;
  success: boolean;
  message?: string;
  data: VirtualSiteVisitItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  stats?: {
    total: number;
    successCount: number;
    failedCount: number;
    pendingCount: number;
  };
}

export const virtualSiteVisitApi = createApi({
  reducerPath: 'virtualSiteVisitApi',
  baseQuery: fetchBaseQuery({
    baseUrl: config().BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('cw-token');
      if (token && token !== 'null' && token !== 'undefined') {
        headers.set('Authorization', `Token ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['virtualSiteVisit'],
  endpoints: (builder) => ({
    // 1. Submit Virtual Site Visit Form (Open / Public)
    submitVirtualSiteVisit: builder.mutation<
      { statusCode: number; success: boolean; message: string; data: VirtualSiteVisitItem; salesforceStatus: string },
      any
    >({
      query: (body) => ({
        url: urlConstants.VIRTUAL_SITE_VISIT_SUBMIT,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['virtualSiteVisit'],
    }),

    // 2. Admin: Get paginated records with filters
    getVirtualSiteVisits: builder.query<
      VirtualSiteVisitListResponse,
      {
        search?: string;
        customerName?: string;
        mobile?: string;
        project?: string;
        salesforceApiStatus?: string;
        startDate?: string;
        endDate?: string;
        source?: string;
        page?: number;
        limit?: number;
      } | void
    >({
      query: (params) => ({
        url: urlConstants.VIRTUAL_SITE_VISIT_LIST,
        method: 'GET',
        params: params || {},
      }),
      providesTags: ['virtualSiteVisit'],
    }),

    // 3. Admin: Get record details by ID
    getVirtualSiteVisitById: builder.query<{ statusCode: number; success: boolean; data: VirtualSiteVisitItem }, string>({
      query: (id) => ({
        url: `${urlConstants.VIRTUAL_SITE_VISIT_GET}/${id}`,
        method: 'GET',
      }),
      providesTags: ['virtualSiteVisit'],
    }),

    // 4. Admin: Export records matching filters
    exportVirtualSiteVisits: builder.query<
      { statusCode: number; success: boolean; data: VirtualSiteVisitItem[]; count: number },
      {
        search?: string;
        customerName?: string;
        mobile?: string;
        project?: string;
        salesforceApiStatus?: string;
        startDate?: string;
        endDate?: string;
        source?: string;
      } | void
    >({
      query: (params) => ({
        url: urlConstants.VIRTUAL_SITE_VISIT_EXPORT,
        method: 'GET',
        params: params || {},
      }),
    }),

    // 5. Admin: Lazy query for export on demand
    lazyExportVirtualSiteVisits: builder.mutation<
      { statusCode: number; success: boolean; data: VirtualSiteVisitItem[]; count: number },
      any
    >({
      query: (params) => ({
        url: urlConstants.VIRTUAL_SITE_VISIT_EXPORT,
        method: 'GET',
        params: params || {},
      }),
    }),

    // 6. Admin: Retry Salesforce call for a record
    retrySalesforceApi: builder.mutation<
      { statusCode: number; success: boolean; message: string; data: VirtualSiteVisitItem },
      string
    >({
      query: (id) => ({
        url: `${urlConstants.VIRTUAL_SITE_VISIT_RETRY_SF}/${id}`,
        method: 'POST',
      }),
      invalidatesTags: ['virtualSiteVisit'],
    }),

    // 7. Get ongoing projects and user emails from Salesforce
    getOngoingProjects: builder.query<
      { statusCode: number; success: boolean; data: { projectNames: string[]; userEmails: string[] } },
      void
    >({
      query: () => ({
        url: urlConstants.VIRTUAL_SITE_VISIT_ONGOING_PROJECTS,
        method: 'GET',
      }),
      providesTags: ['virtualSiteVisit'],
    }),
  }),
});

export const {
  useSubmitVirtualSiteVisitMutation,
  useGetVirtualSiteVisitsQuery,
  useGetVirtualSiteVisitByIdQuery,
  useExportVirtualSiteVisitsQuery,
  useLazyExportVirtualSiteVisitsMutation,
  useRetrySalesforceApiMutation,
  useGetOngoingProjectsQuery,
} = virtualSiteVisitApi;
